/**
 * Hook for getting type-specific display properties for citations.
 *
 * Centralizes the logic for determining what to display based on citation type,
 * making SourceCard and ReferenceCard cleaner and type-safe.
 */

import {
  t as translate,
  useTranslations,
  type TranslateFunction,
} from '@heidi/runtime/i18n';
import type { LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import type { EnrichedReference, SimpleCitation } from '../../contracts';
import { getFormattedSourceName, isFileCitation, isKnownSource } from '../../contracts';
import { resolvePublisherIdentity } from '../../contracts/sourceTypeUtils';
import { getFileSourceIcon } from './sourceIconUtils';

/** Type-specific label for the source (translated string or null) */
export type SourceLabel = string | null;

/** Display properties for a citation */
export interface CitationDisplayProps {
  /** Whether the citation has a URL and can be clicked */
  isClickable: boolean;

  /** Primary display name for the source (prefers short display_name — for inline badges) */
  displayName: string;

  /**
   * Full display name for the source (prefers website_name — for reference rows and hover cards).
   * Falls back to displayName if no website_name is available.
   */
  fullDisplayName: string;

  /** Secondary label based on source type (e.g., "Attached File") */
  sourceLabel: SourceLabel;

  /** Whether this is a file-based citation */
  isFileSource: boolean;

  /** Whether this is an image citation with thumbnail */
  isImageSource: boolean;

  /** File icon component for file-based sources (null for web) */
  FileIcon: LucideIcon | null;

  /** Whether this is a known/trusted web source */
  isKnownSource: boolean;

  /** Whether to show the article title (separate from display name) */
  showTitle: boolean;

  /** Article title to show (may differ from displayName) */
  title: string;

  /**
   * Name beside the favicon on the source card. Publisher abbreviations stay
   * short and uppercase here; reference rows use `fullDisplayName`.
   */
  cardDisplayName: string;
}

function getSourceLabel(
  sourceType: string | undefined,
  t: TranslateFunction
): SourceLabel {
  switch (sourceType) {
    case 'attachment':
      return t('sources.sourceLabels.attachedFile');
    case 'user_document':
      return t('sources.sourceLabels.libraryDocument');
    case 'image':
      return t('sources.sourceLabels.image');
    default:
      return null;
  }
}

/**
 * Compute display properties for a citation.
 * Pure function - no React hooks.
 *
 * EVI-605: Updated to prefer backend-provided enrichment fields:
 * - For user documents: prefer document_title over file_name
 * - For web sources: prefer display_name over domain extraction
 */
function computeCitationDisplay(
  citation: SimpleCitation | EnrichedReference,
  t: TranslateFunction
): CitationDisplayProps {
  const sourceType = citation.source_type || 'web_search';
  const isFileSource = isFileCitation(sourceType);
  const isImageSource = sourceType === 'image';

  // Determine primary display name (short — for inline citation badges)
  let displayName: string;
  // Determine full display name (prefers website_name — for reference rows)
  let fullDisplayName: string;
  let publisherShortForCard = false;
  if (isFileSource) {
    // EVI-605: For user documents, prefer document_title (extracted from PDF metadata)
    // over file_name for a more descriptive display
    if (sourceType === 'user_document' && citation.document_title) {
      displayName = citation.document_title;
    } else {
      // Prefer title (formatted) over file_name (raw filename)
      displayName = citation.title || citation.file_name || '';
    }
    fullDisplayName = displayName;
  } else {
    // EVI-605: For web sources, prefer backend-provided display_name,
    // then website_name, then enriched sourceName, then format from URL
    const enriched = citation as EnrichedReference;
    const publisher = resolvePublisherIdentity({
      url: citation.url,
      domain: citation.domain,
      displayName: citation.display_name ?? enriched.sourceName,
      websiteName: citation.website_name,
    });
    publisherShortForCard = Boolean(publisher?.fullName);
    displayName =
      publisher?.shortName ||
      citation.display_name ||
      citation.website_name ||
      enriched.sourceName ||
      getFormattedSourceName(citation.url || '');
    // Reference rows prefer the publisher's full title, then website_name
    // (e.g. "Australian Journal of General Practice") over the short
    // display_name (e.g. "AustJGenPract(AJGP)").
    fullDisplayName =
      publisher?.fullName ||
      publisher?.shortName ||
      citation.website_name ||
      citation.display_name ||
      enriched.sourceName ||
      getFormattedSourceName(citation.url || '');
  }

  // Get file icon for file-based sources
  const FileIcon = isFileSource
    ? getFileSourceIcon(sourceType, citation.file_name || citation.title)
    : null;

  // Check if it's a known/trusted source (web only)
  const isKnown = !isFileSource && !!citation.url && isKnownSource(citation.url);

  // Determine if we should show the title separately
  // For web sources: always show if available
  // For file sources: don't show (file_name is the display name)
  const showTitle = !isFileSource && !!citation.title;

  // Check if URL is available for clicking
  const isClickable = !!citation.url;

  // Only publishers with a distinct full title (NEJM) keep the short
  // uppercase label on the source card. Everyone else keeps the longer name.
  const cardDisplayName = publisherShortForCard ? displayName : fullDisplayName;

  return {
    isClickable,
    displayName,
    fullDisplayName,
    sourceLabel: getSourceLabel(sourceType, t),
    isFileSource,
    isImageSource,
    FileIcon,
    isKnownSource: isKnown,
    showTitle,
    title: citation.title,
    cardDisplayName,
  };
}

export function useCitationDisplay(
  citation: SimpleCitation | EnrichedReference
): CitationDisplayProps {
  const enriched = citation as EnrichedReference;
  const { t } = useTranslations();
  return useMemo(
    () => computeCitationDisplay(citation, t),
    // Keyed on the fields the output reads rather than on `citation`: callers
    // rebuild the citation object every render, so an identity dependency would
    // make the memo a no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      citation.url,
      citation.title,
      citation.source_type,
      citation.file_name,
      citation.document_title,
      citation.domain,
      citation.website_name,
      citation.display_name,
      enriched.sourceName,
    ]
  );
}

/**
 * Non-hook version for use in non-component contexts. Reads the same adapter
 * through the door's non-React accessor rather than the hook.
 */
export function getCitationDisplayProps(
  citation: SimpleCitation | EnrichedReference
): CitationDisplayProps {
  return computeCitationDisplay(citation, translate);
}
